// Editor-pinned Lovin Malta articles for the homepage left rail —
// guest posts, opinion pieces, analysis features. Renders below
// LatestNewsTiles inside the left rail.
//
// Reads /api/featured (active pins only). Each row already carries
// title / excerpt / thumbnail (the admin endpoint auto-fetches them
// from the Lovin Malta WP REST API on save), so we just present.

import { useEffect, useState } from 'react';
import './FeaturedArticles.css';

interface ApiArticle {
  id: number;
  url: string;
  title: string | null;
  excerpt: string | null;
  thumbnail_url: string | null;
  notes: string | null;
}

interface ApiResponse {
  featured: ApiArticle[];
  fetchedAt?: string;
}

type Status = 'loading' | 'ready' | 'empty' | 'error';

export default function FeaturedArticles() {
  const [status, setStatus] = useState<Status>('loading');
  const [articles, setArticles] = useState<ApiArticle[]>([]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    fetch('/api/featured', { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<ApiResponse>;
      })
      .then((data) => {
        if (cancelled) return;
        setArticles(data.featured ?? []);
        setStatus(data.featured && data.featured.length > 0 ? 'ready' : 'empty');
      })
      .catch((err) => {
        if (cancelled || err?.name === 'AbortError') return;
        console.error('[FeaturedArticles] fetch failed:', err);
        setStatus('error');
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  // Hide the section entirely while empty so the left rail doesn't
  // show a placeholder for the no-pins case.
  if (status === 'empty' || status === 'error') return null;

  return (
    <aside className="featured-articles" aria-label="Featured Lovin Malta articles">
      <header className="featured-articles__header">
        <p className="featured-articles__kicker">Featured</p>
        <h2 className="featured-articles__title">Reads &amp; Analysis</h2>
      </header>
      {status === 'loading' && <SkeletonList />}
      {status === 'ready' && (
        <ul className="featured-articles__list">
          {articles.map((a) => (
            <li key={a.id}>
              <FeaturedTile article={a} />
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

function FeaturedTile({ article }: { article: ApiArticle }) {
  return (
    <a
      className="featured-tile"
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div className="featured-tile__thumb">
        {article.thumbnail_url ? (
          // referrerPolicy="no-referrer" works around Lovin Malta CDN
          // hotlink protection, which returns 403 when the Referer
          // header points at a different host.
          <img
            src={article.thumbnail_url}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="featured-tile__thumb-placeholder">★</span>
        )}
      </div>
      <div className="featured-tile__body">
        <span className="featured-tile__category">Opinion</span>
        <h3 className="featured-tile__title">
          {article.title ?? 'Untitled'}
        </h3>
        {article.excerpt && (
          <p className="featured-tile__excerpt">{article.excerpt}</p>
        )}
      </div>
    </a>
  );
}

function SkeletonList() {
  return (
    <ul className="featured-articles__list" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <li key={i}>
          <div className="featured-tile featured-tile--skeleton">
            <div className="featured-tile__thumb featured-tile__thumb--skeleton" />
            <div className="featured-tile__body">
              <span className="skeleton-line skeleton-line--short" />
              <span className="skeleton-line" />
              <span className="skeleton-line skeleton-line--short" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
